import React, { ChangeEvent, useState, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@apollo/client';
import { Button, makeStyles, Typography } from '@material-ui/core';
import cx from 'clsx';
import { addDays, format, isWithinInterval, isValid, startOfDay } from 'date-fns';
import { TFunction } from 'i18next';
import { ModalContainer, ModalContent, ModalFooter, ModalHeader } from 'components/@common/Modal';
import PageContainer from 'components/@layout/PageContainer';
import BULK_ASSIGN_AVAILABLE_LICENSES from 'graph/license/bulk-assign-available-licenses-mutation';
import GET_MASTERCODE_ACTIVATION_DETAILS from 'graph/orders/get-mastercode-activation-details-query';
import SCHEDULE_UNLOCK_MASTERCODE from 'graph/orders/schedule-unlock-mastercode-mutation';
import SCHEDULE_ORDER_UNLOCK_MASTERCODE from 'graph/orders/schedule-unlock-order-mastercode-mutation';
import UNLOCK_MASTERCODE from 'graph/orders/unlock-mastercode-mutation';
import UNLOCK_ORDER_MASTERCODE from 'graph/orders/unlock-order-mastercode-mutation';
import useStore from 'store';
import { VoidCallback, Theme, AlertOptions } from 'types';
import { ActivationRangeOptions, AlertVariant } from 'types/enums';
import {
  Mastercode,
  Order,
  OrderItemProvisioningResult,
  ProductType,
  BulkAssignAvailableLicenseInput
} from 'types/schema-generated-types';
import { getExpirationDates, isSingleExpirationDate } from '../../utils/activation-date-utils';
import {
  getBulkAssignAvailableCacheUpdate,
  getMyOrdersMutationCacheUpdate
} from '../../utils/cacheUpdater';
import { ORDER_DATE_FORMAT } from '../../utils/constants';
import getOrderNumber from '../../utils/get-order-number';
import MastercodeModalDetails from '../components/MastercodeModalDetails';
import ActivateMastercodeForm from './components/ActivateMastercodeForm';

interface ActivateMastercodeModalProps {
  activateAll: boolean;
  mastercode: Mastercode | null;
  order: Order;
  onClose: VoidCallback;
  onSubmit?: VoidCallback;
}

const nowDate = new Date();
nowDate.setHours(0, 0, 0, 0);
const minDate = addDays(nowDate, 1);
const maxDate = addDays(nowDate, 90);

const getSuccessAlert = (
  t: TFunction,
  classes: Record<string, string>,
  html: ReactElement
): AlertOptions => ({
  variant: AlertVariant.SUCCESS,
  title: t('mastercodeActivated'),
  html,
  confirmButtonText: t('Close')
});

const getWarningAlert = (
  t: TFunction,
  classes: Record<string, string>,
  html: ReactElement
): AlertOptions => ({
  variant: AlertVariant.WARNING,
  title: t('confirmMastercodeActivation'),
  html,
  confirmButtonText: t('Activate')
});

const ActivateMastercodeModal = ({
  activateAll,
  order,
  mastercode,
  onClose,
  onSubmit: onSubmitHandlerProp
}: ActivateMastercodeModalProps) => {
  const classes = useStyles();
  const [{ user }, { closeModal, openAlert }] = useStore();
  const { t } = useTranslation('Order');
  const now = new Date();

  const [unlockMastercode, { loading: unlockMastercodeLoading }] = useMutation(
    UNLOCK_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [scheduleUnlockMastercode, { loading: scheduleUnlockMastercodeLoading }] = useMutation(
    SCHEDULE_UNLOCK_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [unlockOrderMastercodes, { loading: unlockOrderMastercodesLoading }] = useMutation(
    UNLOCK_ORDER_MASTERCODE,
    getMyOrdersMutationCacheUpdate(user.me?.id)
  );

  const [
    scheduleUnlockOrderMastercodes,
    { loading: scheduleUnlockOrderMastercodesLoading }
  ] = useMutation(SCHEDULE_ORDER_UNLOCK_MASTERCODE, getMyOrdersMutationCacheUpdate(user.me?.id));

  const [
    bulkAssignAvailableLicenses,
    { loading: bulkAssignAvailableLicensesLoading }
  ] = useMutation(BULK_ASSIGN_AVAILABLE_LICENSES, getBulkAssignAvailableCacheUpdate(user.me?.id));

  const [activationRangeDate, setActivationRangeDate] = useState<string>(
    ActivationRangeOptions.NOW
  );

  const [activationDate, setActivationDate] = useState<null | Date>(addDays(startOfDay(now), 1));

  const { data: activationDetails, loading: mastercodeActivationDetailsLoading } = useQuery(
    GET_MASTERCODE_ACTIVATION_DETAILS,
    {
      variables: {
        where: { orders: { id: order.id } },
        expirationWhere: { dateUnlocked: activationDate }
      },
      skip: !activationDate
    }
  );

  const [expirationDates, isSingleExpiration] = useMemo(() => {
    let mastercodesToActivate: Mastercode[] = activationDetails?.mastercodes ?? [];
    if (!activateAll && mastercode) {
      mastercodesToActivate = mastercodesToActivate.filter(m => m.id === mastercode.id);
    }

    const expirationDates = getExpirationDates(mastercodesToActivate);
    const isSingleExpiration = isSingleExpirationDate(expirationDates);

    return [expirationDates, isSingleExpiration];
  }, [activateAll, activationDetails, mastercode]);

  const confirmationWarning = isSingleExpiration ? (
    t('expireDate', { date: format(expirationDates[0].date, ORDER_DATE_FORMAT) })
  ) : (
    <ul className={cx(classes.mastercodeList, classes.alertList)}>
      {expirationDates.map(({ code, date }) => (
        <li key={code}>
          {t('mastercode', { code })}
          <br />
          {t('expireDate', { date: format(date, ORDER_DATE_FORMAT) })}
        </li>
      ))}
    </ul>
  );

  const formWarning = isSingleExpiration
    ? activationDate &&
      isValid(activationDate) &&
      t('expireDateWarning', {
        activateDate: format(activationDate, ORDER_DATE_FORMAT),
        expireDate: format(expirationDates[0].date, ORDER_DATE_FORMAT)
      })
    : activationDate &&
      isValid(activationDate) && (
        <>
          {t('expireDatesWarning', { activateDate: format(activationDate, ORDER_DATE_FORMAT) })}
          <ul className={classes.mastercodeList}>
            {expirationDates.map(({ code, date }) => (
              <li key={code}>
                {t('mastercode', { code })}
                <br />
                {t('expireDate', { date: format(date, ORDER_DATE_FORMAT) })}
              </li>
            ))}
          </ul>
        </>
      );

  const orderWarningAlertHTML = (
    <div>
      <p>{t('orderNumber', { orderNumber: getOrderNumber(order) })}</p>
      <p>
        {t('activationDate', {
          date:
            activationDate && isValid(activationDate)
              ? format(activationDate, ORDER_DATE_FORMAT)
              : ''
        })}
      </p>
      <p>{activationDate && isValid(activationDate) ? confirmationWarning : ''}</p>
    </div>
  );

  const orderSuccessAlertHTML: ReactElement = <>{orderWarningAlertHTML}</>;

  const handleDateActivationChange = (date: Date | null) => setActivationDate(date);

  const doMastercodeUnlock = async (mastercode: Mastercode) => {
    const { data } = await unlockMastercode({
      variables: { mastercodeId: mastercode.id }
    });

    const unlockResult = data.unlockMastercode;
    if (unlockResult?.success) {
      await assignLicenses(unlockResult.success);
    }
  };

  const doOrderUnlock = async (order: Order) => {
    const { data } = await unlockOrderMastercodes({
      variables: { orderId: order.id }
    });

    const unlockResult = data.unlockOrderMastercodes;
    if (unlockResult?.success) {
      await assignLicenses(unlockResult.success);
    }
  };

  const assignLicenses = async (orderItemProvisioningResults: OrderItemProvisioningResult[]) => {
    const assignRequests = orderItemProvisioningResults.reduce(
      (acc, orderItemProvisioningResult) => {
        const { orderItem } = orderItemProvisioningResult;
        if (orderItem?.product?.type === ProductType.USER) {
          const request = {
            userId: user.me?.id as number,
            orderItemId: [orderItem.id],
            action: 'ASSIGN',
            skipAlreadyLicensed: true
          };
          acc.push(request);
        }
        return acc;
      },
      [] as BulkAssignAvailableLicenseInput[]
    );

    if (assignRequests.length > 0) {
      await bulkAssignAvailableLicenses({ variables: { assignRequests } });
    }
  };

  const handleActivationChange = (event: ChangeEvent<HTMLInputElement>) =>
    setActivationRangeDate((event.target as HTMLInputElement).value);

  const handleOnConfirmMastercodeModal = async (mastercode: Mastercode) => {
    try {
      if (activationRangeDate === ActivationRangeOptions.LATER) {
        await scheduleUnlockMastercode({
          variables: { mastercodeId: mastercode.id, date: activationDate }
        });
      } else {
        await doMastercodeUnlock(mastercode);
      }
      openAlert(getSuccessAlert(t, classes, orderSuccessAlertHTML));
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const handleOnConfirmOrderModal = async (order: Order) => {
    try {
      if (activationRangeDate === ActivationRangeOptions.LATER) {
        await scheduleUnlockOrderMastercodes({
          variables: { orderId: order.id, date: activationDate }
        });
      } else {
        await doOrderUnlock(order);
      }
      openAlert(getSuccessAlert(t, classes, orderSuccessAlertHTML));
    } catch (error) {
      //TODO: Handle Error
      console.log('Error', error);
    }
  };

  const submitForm = async () => {
    openAlert({
      ...getWarningAlert(t, classes, orderWarningAlertHTML),
      onSubmit: async result => {
        if (!result.value) return;
        if (activateAll) await handleOnConfirmOrderModal(order);
        else await handleOnConfirmMastercodeModal(mastercode as Mastercode);
        if (onSubmitHandlerProp) await onSubmitHandlerProp();

        closeModal();
      }
    });
  };

  const isLoading =
    bulkAssignAvailableLicensesLoading ||
    scheduleUnlockMastercodeLoading ||
    scheduleUnlockOrderMastercodesLoading ||
    unlockMastercodeLoading ||
    unlockOrderMastercodesLoading ||
    mastercodeActivationDetailsLoading;

  const isValidDate = useMemo(() => {
    if (activationRangeDate !== ActivationRangeOptions.LATER) return true;
    if (!activationDate || !isValid(activationDate)) return false;

    return isWithinInterval(activationDate, { start: minDate, end: maxDate });
  }, [activationRangeDate, activationDate]);

  return (
    <PageContainer showChildrenWhileLoading loading={isLoading}>
      <ModalContainer
        Header={
          <ModalHeader>
            <Typography noWrap variant="h2" component="h1">
              {t('orderNumber', { orderNumber: getOrderNumber(order) })}
            </Typography>
          </ModalHeader>
        }
        Footer={
          <ModalFooter
            Actions={
              <>
                <Button
                  aria-label={t('Cancel')}
                  color="secondary"
                  variant="outlined"
                  disableElevation
                  onClick={onClose}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  aria-label={t('Confirm & Activate')}
                  variant="contained"
                  color="primary"
                  disabled={!isValidDate || isLoading}
                  disableElevation
                  onClick={submitForm}
                >
                  {t('Confirm & Activate')}
                </Button>
              </>
            }
          />
        }
      >
        <ModalContent className={classes.body}>
          <MastercodeModalDetails
            isActivateCode
            order={order}
            mastercode={mastercode ?? order.mastercodes ?? []}
          />
          <ActivateMastercodeForm
            nowDate={nowDate}
            minDate={minDate}
            maxDate={maxDate}
            isValidDate={isValidDate}
            warningText={formWarning}
            activationRangeValue={activationRangeDate}
            activationRangeDate={activationDate}
            handleActivationChange={handleActivationChange}
            handleDateActivationChange={handleDateActivationChange}
          />
        </ModalContent>
      </ModalContainer>
    </PageContainer>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  body: {
    padding: theme.spacing(1)
  },
  mastercodeList: {
    margin: '0 0',
    paddingInlineStart: '15px',
    '& > li': { marginTop: 10 }
  },
  alertList: {
    display: 'inline-block',
    padding: '0 15px',
    textAlign: 'left'
  }
}));

export default ActivateMastercodeModal;
